import React from 'react';
import { Box } from '@osmosis/react-ui/components/Box';
import { Flex } from '@osmosis/react-ui/components/Box';
import { Icon } from '@osmosis/react-ui/components/Icon';
import { useColors } from '@osmosis/react-ui/hooks/useColors';
import CTAs from '../block-ui/CTAs';
import RichText from '../block-ui/RichText';
import BlockWrapper from '../BlockWrapper';
import {
  ComposerBlockElement,
  FeatureListNewBlockProps,
  FeatureListNewColumn,
} from '../../../types/composer';
import Heading from '../block-ui/Heading';
import Subheading from '../block-ui/Subheading';
import headingSchema from '../../../lib/composer/schemas/heading';
import subheadingSchema from '../../../lib/composer/schemas/subheading';
import Title from '../block-ui/Title';
import { ctasSchema } from '../../../lib/composer/schemas/cta';
import columnsSchema, {
  featureIconMap,
} from '../../../lib/composer/schemas/featureListNew';
import themeSchema from '../../../lib/composer/schemas/theme';
import textSchema from '../../../lib/composer/schemas/text';

type FeatureNewColumnProps = {
  theme: string;
  column: FeatureListNewColumn;
};

const FeatureNewColumn = (props: FeatureNewColumnProps) => {
  const { column, theme } = props;
  const colors = useColors();
  return (
    <Flex p={3} flexDirection="column" justifyContent="center" sx={{ gap: 1 }}>
      <Flex alignItems="center" minWidth="50px" marginBottom={3}>
        {column.icon && (
          <Box marginRight={2} minWidth="initial">
            <Icon
              icon={featureIconMap[column?.icon?.name]}
              size="2xl"
              color={colors.orange}
              aria-hidden="true"
              role="img"
            />
          </Box>
        )}
        <Title
          text={column.title}
          theme={theme}
          textProps={{
            fontSize: '18px',
            color: colors.blue,
          }}
        />
      </Flex>
      <Box>
        <RichText
          html={column.features}
          theme={theme}
          containerProps={{
            sx: {
              fontSize: '14px',
            },
            css: {
              ul: {
                margin: 0,
                padding: 0,
                paddingLeft: 25,
                li: {
                  marginBottom: 8,
                },
              },
            },
          }}
        />
      </Box>
    </Flex>
  );
};

type FeatureListNewProps = FeatureListNewBlockProps;

/**
 * FeatureListNew block.
 */
const FeatureListNew: ComposerBlockElement = (props: FeatureListNewProps) => {
  const {
    heading,
    subheading,
    columns,
    text,
    theme = '',
    cta: ctaProps,
    ctas: ctasProps,
    anchor,
    topHeader,
  } = props;

  const ctas = [...(ctaProps?.caption ? [ctaProps] : []), ...(ctasProps || [])];

  const colors = useColors();

  return (
    <Flex bg={colors[theme] || 'bg-0'} id={anchor}>
      <BlockWrapper width="100%">
        <Box>
          {heading && (
            <Heading
              text={heading}
              theme={theme}
              topHeader={topHeader}
              textProps="text-center mb-3"
            />
          )}
          {subheading && (
            <Subheading
              text={subheading}
              theme={theme}
              textProps="text-center mb-2"
              topHeader={topHeader}
            />
          )}
          <Box
            width="100%"
            sx={{
              display: 'grid',
              gridGap: 3,
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            }}
          >
            {columns?.map((items, index) => (
              <Flex
                alignItems="flex-start"
                key={`item-${index}`}
                flexDirection="column"
              >
                {items.map((column) => (
                  <FeatureNewColumn
                    key={column.title}
                    theme={theme}
                    column={column}
                  />
                ))}
              </Flex>
            ))}
          </Box>
          <Flex flexDirection="column" alignItems="center" sx={{ gap: 3 }}>
            {text && <RichText html={text} theme={theme} />}
            <CTAs ctas={ctas} theme={theme} />
          </Flex>
        </Box>
      </BlockWrapper>
    </Flex>
  );
};

FeatureListNew.schema = {
  name: 'feature-list-new',
  label: 'Feature List New',
  editableProps: [
    headingSchema,
    subheadingSchema,
    columnsSchema,
    textSchema,
    ctasSchema,
    themeSchema,
  ],
};

export default FeatureListNew;
